import io from 'socket.io-client'
import { getCustomerPanelUrl } from './panelUrls'
import { joinApiBase, resolveCustomerApiUrl } from './customerApiBase'

const productionApiUrl = 'https://myshop-api-qfpr.onrender.com'
const localApiUrl = 'http://localhost:5000'

const isLocalHost = () =>
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname)

export const getApiUrl = () => resolveCustomerApiUrl({
  hostname: typeof window !== 'undefined' ? window.location.hostname : '',
  origin: typeof window !== 'undefined' ? window.location.origin : '',
  envApiUrl: process.env.REACT_APP_API_URL || ''
})

export const getAuthApiUrl = getApiUrl

const app_url = getCustomerPanelUrl()
const api_url = getApiUrl()
const auth_api_url = getAuthApiUrl()

export const PUBLIC_API_BASE = joinApiBase(api_url)
export const AUTH_API_BASE = joinApiBase(auth_api_url)

const socketTokenRefreshSkewMs = 10 * 1000
let socketTokenState = null
let socketTokenPromise = null
let customerSocketEnabled = false
let socketConnectionStarted = false

const clearSocketToken = () => {
  socketTokenState = null
  socketTokenPromise = null
}

const getSocketToken = async () => {
  if (
    socketTokenState?.token &&
    socketTokenState.expiresAt > Date.now() + socketTokenRefreshSkewMs
  ) {
    return socketTokenState.token
  }

  if (socketTokenPromise) return socketTokenPromise

  socketTokenPromise = fetch(`${AUTH_API_BASE}/customer/socket-token`, {
    method: 'GET',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'X-Client-App': 'customer' }
  })
    .then(async response => {
      if (!response.ok) {
        const error = new Error('Socket authentication failed')
        error.status = response.status
        throw error
      }

      const data = await response.json()
      const expiresInSeconds = Number(data?.expiresInSeconds)
      if (!data?.socketToken || !Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
        throw new Error('Invalid socket authentication response')
      }

      socketTokenState = {
        token: data.socketToken,
        expiresAt: Date.now() + expiresInSeconds * 1000
      }
      return socketTokenState.token
    })
    .catch(error => {
      clearSocketToken()
      if (error?.status === 401 || error?.status === 403) customerSocketEnabled = false
      throw error
    })
    .finally(() => {
      socketTokenPromise = null
    })

  return socketTokenPromise
}

const provideSocketAuth = async (done) => {
  if (!customerSocketEnabled) return done({})

  try {
    const token = await getSocketToken()
    return done({ token })
  } catch (_) {
    return done({})
  }
}

// Vercel rewrites do not proxy WebSocket upgrades; sockets continue to use Render.
const socket = io(isLocalHost() ? localApiUrl : productionApiUrl, {
  autoConnect: false,
  withCredentials: true,
  transports: ['websocket', 'polling'],
  auth: provideSocketAuth,
  reconnection: true,
  reconnectionAttempts: 5
})

socket.on('connect_error', () => {
  clearSocketToken()
})

socket.on('disconnect', reason => {
  if (reason === 'io server disconnect') socketConnectionStarted = false
})

socket.io?.on('reconnect_failed', () => {
  socketConnectionStarted = false
})

export const initSocket = () => {
  customerSocketEnabled = true
  if (!socketConnectionStarted) {
    socketConnectionStarted = true
    socket.connect()
  }
  return socket
}

// Backward-compatible alias for older customer components.
export const initSocketUser = () => initSocket()

export const disconnectSocket = () => {
  customerSocketEnabled = false
  const shouldDisconnect = socketConnectionStarted || socket.connected || socket.active
  socketConnectionStarted = false
  clearSocketToken()
  if (shouldDisconnect) socket.disconnect()
}

export {
  socket,
  app_url,
  api_url,
  auth_api_url
}
